import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaCheck, FaLightbulb } from "react-icons/fa";

const Contact = ({ mode = "combined", title = null, subtitle = null }) => {
  const [formType, setFormType] = useState("it");
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [itData, setItData] = useState({
    form_type: "it",
    fullname: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    description: "",
    deadline: "",
    file: null,
  });

  const [dmData, setDmData] = useState({
    form_type: "dm",
    fullname: "",
    brand: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    goals: [],
    description: "",
    file: null,
  });

  // Service options mapped to backend values
  const itServices = [
    { value: "web_development", label: "Web Development" },
    { value: "app_development", label: "App Development" },
    { value: "software_development", label: "Software Development" },
    { value: "cloud_solutions", label: "Cloud Solutions" },
    { value: "it_consulting", label: "IT Consulting" },
    { value: "automation_ai", label: "Automation/AI Integration" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "other_it", label: "Other" },
  ];

  const dmServices = [
    { value: "social_media_marketing", label: "Social Media Marketing" },
    { value: "performance_marketing", label: "Performance Marketing (Ads)" },
    { value: "seo", label: "SEO" },
    { value: "branding_design", label: "Branding & Creative Design" },
    { value: "content_marketing", label: "Content Marketing" },
    { value: "lead_generation", label: "Lead Generation" },
    { value: "website_marketing", label: "Website Marketing" },
    { value: "marketing_automation", label: "Marketing Automation" },
    { value: "all_in_one", label: "All-in-One Digital Marketing" },
    { value: "other_dm", label: "Other" },
  ];

  // Budget options mapped to backend values
  const itBudgets = [
    { value: "it_25_50k", label: "₹25,000 – ₹50,000" },
    { value: "it_50_100k", label: "₹50,000 – ₹1,00,000" },
    { value: "it_100_500k", label: "₹1,00,000 – ₹5,00,000" },
    { value: "it_500k_plus", label: "₹5,00,000+" },
  ];

  const dmBudgets = [
    { value: "dm_20_50k", label: "₹20,000 – ₹50,000" },
    { value: "dm_50_100k", label: "₹50,000 – ₹1,00,000" },
    { value: "dm_100_500k", label: "₹1,00,000 – ₹5,00,000" },
    { value: "dm_500k_plus", label: "₹5,00,000+" },
  ];

  // Business goals mapped to backend values
  const businessGoals = [
    { value: "increase_leads", label: "Increase Leads" },
    { value: "brand_awareness", label: "Improve Brand Awareness" },
    { value: "social_media_growth", label: "Grow Social Media" },
    { value: "website_traffic", label: "Increase Website Traffic" },
    { value: "scale_sales", label: "Scale Sales" },
  ];

  const handleITChange = (e) => {
    const { name, value, files } = e.target;
    setItData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleDMChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (type === "checkbox") {
      setDmData((prev) => {
        const newGoals = checked
          ? [...prev.goals, value]
          : prev.goals.filter((g) => g !== value);
        return { ...prev, goals: newGoals };
      });
      return;
    }

    setDmData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleITSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for file upload
      const formData = new FormData();

      // Add all IT form fields
      Object.keys(itData).forEach((key) => {
        if (key === "file" && itData[key]) {
          formData.append(key, itData[key]);
        } else if (itData[key] !== null && itData[key] !== "") {
          formData.append(key, itData[key]);
        }
      });

      const response = await fetch(
        "https://amplinova.pythonanywhere.com/api/contact/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        // Reset form
        setItData({
          form_type: "it",
          fullname: "",
          company: "",
          email: "",
          phone: "",
          service: "",
          budget: "",
          description: "",
          deadline: "",
          file: null,
        });
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
      } else {
        const errorData = await response.json();
        console.error("Submission error:", errorData);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error("Network error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDMSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare form data for file upload
      const formData = new FormData();

      // Add all DM form fields
      Object.keys(dmData).forEach((key) => {
        if (key === "file" && dmData[key]) {
          formData.append(key, dmData[key]);
        } else if (key === "goals") {
          // Handle goals array
          dmData.goals.forEach((goal) => {
            formData.append("goals", goal);
          });
        } else if (dmData[key] !== null && dmData[key] !== "") {
          formData.append(key, dmData[key]);
        }
      });

      const response = await fetch(
        "https://amplinova.pythonanywhere.com/api/contact/",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        // Reset form
        setDmData({
          form_type: "dm",
          fullname: "",
          brand: "",
          email: "",
          phone: "",
          service: "",
          budget: "",
          goals: [],
          description: "",
          file: null,
        });
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
      } else {
        const errorData = await response.json();
        console.error("Submission error:", errorData);
        setSubmitStatus("error");
      }
    } catch (err) {
      console.error("Network error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delayChildren: 0.3, staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const getMainTitle = () => {
    if (title) return title;
    switch (mode) {
      case "it":
        return "Let's Build Your Tech Solution";
      case "marketing":
        return "Boost Your Digital Growth";
      default:
        return "Start Your Project Today";
    }
  };

  const getSubtitle = () => {
    if (subtitle) return subtitle;
    switch (mode) {
      case "it":
        return "Get expert IT solutions tailored to your business needs.";
      case "marketing":
        return "Drive results with data-driven marketing strategies.";
      default:
        return "Transform your vision into reality. Share your details and we'll get back to you within 24 hours.";
    }
  };

  const getBenefits = () => {
    switch (mode) {
      case "it":
        return [
          "Expert technical consultation",
          "Custom software development",
          "24/7 support & maintenance",
          "Scalable cloud solutions",
        ];
      case "marketing":
        return [
          "Data-driven strategy session",
          "Competitor analysis report",
          "Custom marketing plan",
          "ROI-focused campaigns",
        ];
      default:
        return [
          "Free project consultation",
          "Detailed proposal within 24h",
          "Dedicated project manager",
          "Flexible engagement models",
        ];
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-white text-gray-800 py-12 lg:py-16"
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:56px_56px]" />
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full filter blur-3xl opacity-5" />
        <div className="absolute bottom-0 right-0 w-60 h-60 bg-orange-50 rounded-full filter blur-3xl opacity-30" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          {/* 25% LEFT | 75% RIGHT */}
          <div className="grid grid-cols-1 lg:grid-cols-[25%_75%] gap-8 items-start">
            {/* LEFT PANEL — SHIFTED DOWN 100px ON ALL SCREENS */}
            <motion.div
              variants={itemVariants}
              className="space-y-6 mt-[100px]"
            >
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold">
                  {getMainTitle()}
                </h2>
                <p className="text-lg text-gray-600">{getSubtitle()}</p>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <FaLightbulb className="text-yellow-500" /> What you'll get:
                </h3>

                <ul className="space-y-3">
                  {getBenefits().map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FaCheck className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                    <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      Join 500+ satisfied clients
                    </p>
                    <p className="text-xs text-gray-600">
                      Trusted by businesses worldwide
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* RIGHT PANEL (FORM CONTAINER) */}
            <motion.div variants={itemVariants} className="pt-0 mt-0">
              <div className="bg-white p-8 rounded-md border border-gray-300 shadow-md w-full">
                {/* TAB SWITCHER */}
                <div className="flex w-full mb-8">
                  <button
                    onClick={() => setFormType("it")}
                    className={`w-1/2 py-3 font-semibold rounded-l-md border ${
                      formType === "it"
                        ? "bg-amber-500 text-white border-amber-600"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                  >
                    IT Services
                  </button>

                  <button
                    onClick={() => setFormType("dm")}
                    className={`w-1/2 py-3 font-semibold rounded-r-md border ${
                      formType === "dm"
                        ? "bg-amber-500 text-white border-amber-600"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                  >
                    Digital Marketing
                  </button>
                </div>

                {/* SUCCESS / ERROR MESSAGES */}
                {submitStatus === "success" && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md text-green-700">
                    Thank you! Your inquiry has been submitted successfully.
                    We'll contact you within 24 hours.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                    Something went wrong. Please try again or contact us
                    directly.
                  </div>
                )}

                {/* IT FORM */}
                {formType === "it" && (
                  <form onSubmit={handleITSubmit} className="space-y-6">
                    <h3 className="text-2xl font-bold mb-4">
                      IT Development & Tech Solutions
                    </h3>

                    {/* Full Name + Company */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="fullname"
                          placeholder="Enter your Full Name"
                          value={itData.fullname}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          name="company"
                          placeholder="Enter your Company Name"
                          value={itData.company}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        />
                      </div>
                    </div>

                    {/* Email + Phone SIDE BY SIDE */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter your Email"
                          value={itData.email}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Enter your Phone Number"
                          value={itData.phone}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        />
                      </div>
                    </div>

                    {/* Service Required + Budget SIDE BY SIDE */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Required *
                        </label>
                        <select
                          name="service"
                          value={itData.service}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 focus:outline-none focus:border-gray-700"
                          required
                        >
                          <option value="">Select Service</option>
                          {itServices.map((service) => (
                            <option key={service.value} value={service.value}>
                              {service.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Project Budget *
                        </label>
                        <select
                          name="budget"
                          value={itData.budget}
                          onChange={handleITChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 focus:outline-none focus:border-gray-700"
                          required
                        >
                          <option value="">Select Budget</option>
                          {itBudgets.map((budget) => (
                            <option key={budget.value} value={budget.value}>
                              {budget.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                   
                    

                    {/* Project Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Description *
                      </label>
                      <textarea
                        name="description"
                        placeholder="Enter details about your project requirements, features, and objectives..."
                        rows="3"
                        value={itData.description}
                        onChange={handleITChange}
                        className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        required
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-md font-semibold transition-colors duration-300"
                    >
                      {isSubmitting ? "Submitting..." : "Request Proposal"}
                    </button>
                  </form>
                )}

                {/* DIGITAL MARKETING FORM */}
                {formType === "dm" && (
                  <form onSubmit={handleDMSubmit} className="space-y-6">
                    <h3 className="text-2xl font-bold mb-4">
                      Digital Marketing, Branding & Growth Solutions
                    </h3>

                    {/* Name + Brand */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="fullname"
                          placeholder="Enter your Full Name"
                          value={dmData.fullname}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business Name
                        </label>
                        <input
                          type="text"
                          name="brand"
                          placeholder="Enter your Business Name"
                          value={dmData.brand}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        />
                      </div>
                    </div>

                    {/* Email + Phone SIDE BY SIDE */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="Enter your Email"
                          value={dmData.email}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Enter your Phone Number"
                          value={dmData.phone}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        />
                      </div>
                    </div>

                    {/* SERVICE + BUDGET SIDE BY SIDE */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Interested In *
                        </label>
                        <select
                          name="service"
                          value={dmData.service}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 focus:outline-none focus:border-gray-700"
                          required
                        >
                          <option value="">Select Service</option>
                          {dmServices.map((service) => (
                            <option key={service.value} value={service.value}>
                              {service.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monthly Marketing Budget *
                        </label>
                        <select
                          name="budget"
                          value={dmData.budget}
                          onChange={handleDMChange}
                          className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 focus:outline-none focus:border-gray-700"
                          required
                        >
                          <option value="">Select Budget</option>
                          {dmBudgets.map((budget) => (
                            <option key={budget.value} value={budget.value}>
                              {budget.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* BUSINESS GOALS — 2 COLUMN CHECKBOXES */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Business Goals *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        {businessGoals.map((goal) => (
                          <label
                            key={goal.value}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-amber-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              name="goals"
                              value={goal.value}
                              checked={dmData.goals.includes(goal.value)}
                              onChange={handleDMChange}
                              className="w-4 h-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium">
                              {goal.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Marketing Needs Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Describe Your Marketing Needs *
                      </label>
                      <textarea
                        name="description"
                        placeholder="Tell us about your business, target audience, current marketing efforts, and what you hope to achieve..."
                        rows="3"
                        value={dmData.description}
                        onChange={handleDMChange}
                        className="w-full px-4 py-3 border border-gray-400 rounded-md caret-black text-gray-900 placeholder-gray-500 focus:outline-none focus:border-black"
                        required
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-md font-semibold transition-colors duration-300"
                    >
                      {isSubmitting
                        ? "Submitting..."
                        : "Get Marketing Strategy"}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
